import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { rpcClient } from '@/api/rpc/client';
import {
  ReextractSalesOrderDraftInput,
  ReextractSalesOrderDraftResponse,
} from '@erp/rpc';

interface UseReextractSalesOrderDraftOptions {
  onSuccess?: (result: ReextractSalesOrderDraftResponse) => void;
  onError?: (error: Error) => void;
}

export function useReextractSalesOrderDraft(
  options: UseReextractSalesOrderDraftOptions = {},
) {
  const [showSaveNoteDialog, setShowSaveNoteDialog] = useState(false);
  const [lastUserNote, setLastUserNote] = useState<string>('');

  const reextractMutation = useMutation({
    mutationFn: async (input: ReextractSalesOrderDraftInput) => {
      const result =
        await rpcClient.accountsReceivable.stagedSalesOrders.reExtract(input);
      return result;
    },
    onSuccess: (result) => {
      // If re-extraction was successful and there was a user note, offer to save it to Memory Bank
      if (result.success && result.userContextNote?.trim()) {
        setLastUserNote(result.userContextNote);
        setShowSaveNoteDialog(true);
      }
      options.onSuccess?.(result);
    },
    onError: (error) => {
      console.error('Failed to re-extract sales order draft:', error);
      options.onError?.(
        error instanceof Error ? error : new Error('Unknown error'),
      );
    },
  });

  const reextract = useCallback(
    (params: {
      inboundEmailId: string;
      classificationId?: string;
      userContextNoteMarkdown?: string;
      refreshOCR?: boolean;
    }) => {
      return reextractMutation.mutateAsync({
        inboundEmailId: params.inboundEmailId,
        classificationId: params.classificationId,
        userContextNoteMarkdown: params.userContextNoteMarkdown,
        refreshOCR: params.refreshOCR ?? false,
      });
    },
    [reextractMutation],
  );

  const dismissSaveNoteDialog = useCallback(() => {
    setShowSaveNoteDialog(false);
    setLastUserNote('');
  }, []);

  return {
    reextract,
    isReextracting: reextractMutation.isPending,
    error: reextractMutation.error,

    // Memory bank note saving state
    showSaveNoteDialog,
    lastUserNote,
    dismissSaveNoteDialog,
  };
}
